/**
 * Users Management Page
 * Search, filter, and manage user records
 */

import React, { useState, useEffect } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import { Search, Filter, Download, ChevronLeft, ChevronRight, MoreVertical, User, Mail, Calendar, Shield, Award, Ban, Eye, KeyRound, UserCheck, Trash2, ShieldOff, ListChecks, Check, RotateCcw } from 'lucide-react';

const UsersPage = () => {
  const { api } = useAdmin();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [accountType, setAccountType] = useState('real'); // Real (default) / Test / QA / Bot / All
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  
  // GEMS Tag editing state
  const [showEditTagModal, setShowEditTagModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newGemsTag, setNewGemsTag] = useState('');
  const [savingTag, setSavingTag] = useState(false);
  
  // Round 15: Get Started checklist override
  const [showTasksModal, setShowTasksModal] = useState(false);
  const [tasksUser, setTasksUser] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [taskBusy, setTaskBusy] = useState('');

  // Badge editing state
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [badgeUser, setBadgeUser] = useState(null);
  const [selectedBadge, setSelectedBadge] = useState('');
  const [savingBadge, setSavingBadge] = useState(false);

  // Password reset state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [isTemporary, setIsTemporary] = useState(false);
  const [forceChange, setForceChange] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [page, searchQuery, accountType]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '20'
      });
      if (searchQuery) {
        params.append('query', searchQuery);
      }
      if (accountType) {
        params.append('account_type', accountType);
      }
      
      const response = await api.get(`/api/admin/users?${params}`);
      setUsers(response.data.users);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const exportUsers = async () => {
    try {
      const response = await api.get('/api/admin/users/export', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'users_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const viewUserDetail = async (userId) => {
    try {
      const response = await api.get(`/api/admin/users/${userId}`);
      setSelectedUser(response.data);
      setShowUserModal(true);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  // Round 13: suspend = 7-day lockout that takes effect immediately; ban =
  // permanent. (The old icon sent "warning", which locked people out with
  // no end date.)
  const suspendUser = async (userId, type) => {
    const label = type === 'temporary' ? 'Suspend this user for 7 days? They lose access immediately.' : 'Send this user a warning?';
    if (!window.confirm(label)) return;
    const reason = window.prompt('Reason (shown to the user):', 'Violation of Community Guidelines') || 'Violation of Community Guidelines';
    try {
      await api.post(`/api/admin/users/${userId}/suspend`, {
        user_id: userId,
        suspension_type: type,
        reason,
        duration_days: type === 'temporary' ? 7 : null
      });
      toast.success(type === 'warning' ? 'Warning sent' : 'User suspended for 7 days');
      fetchUsers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to suspend user');
    }
  };
  const banUser = async (user) => {
    const label = user.name || user.handle || user.email;
    if (!window.confirm(`PERMANENTLY ban ${label}? They lose access immediately and their posts are hidden.`)) return;
    const reason = window.prompt('Reason (shown to the user):', 'Violation of Community Guidelines') || 'Violation of Community Guidelines';
    try {
      await api.post(`/api/admin/users/${user.id}/ban`, { reason });
      toast.success(`${label} banned`);
      fetchUsers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to ban user');
    }
  };

  // Round 10: an accidental suspension had no way back from this table, and
  // there was no way to remove an account at all.
  const unsuspendUser = async (user) => {
    if (!window.confirm(`Lift the suspension on ${user.name || user.handle}? They can log in again right away.`)) return;
    try {
      await api.post(`/api/admin/users/${user.id}/unsuspend`);
      toast.success('User unsuspended');
      fetchUsers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to unsuspend user');
    }
  };

  const deleteUser = async (user) => {
    const label = user.name || user.handle || user.email;
    if (!window.confirm(`PERMANENTLY delete ${label}?\n\nThis removes their account, posts, messages, follows, streams and wallet history. This cannot be undone.`)) return;
    const typed = window.prompt(`Type DELETE to confirm removing ${label}:`);
    if (typed !== 'DELETE') { toast.info('Delete cancelled'); return; }
    try {
      await api.delete(`/api/admin/users/${user.id}`);
      toast.success(`${label} deleted`);
      fetchUsers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete user');
    }
  };

  const assignBadge = async (userId, badge) => {
    setSavingBadge(true);
    try {
      await api.put(`/api/admin/users/${userId}/badge`, {
        badge_type: badge || null
      });
      toast.success(badge ? `${badge} badge assigned` : 'Badge removed');
      setShowBadgeModal(false);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to assign badge');
    } finally {
      setSavingBadge(false);
    }
  };
  
  // ── Get Started checklist (Round 15) ──
  const openTasksModal = async (user) => {
    setTasksUser(user);
    setTasks(null);
    setShowTasksModal(true);
    setTasksLoading(true);
    try {
      const res = await api.get(`/api/admin/users/${user.id}/onboarding`);
      setTasks(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load the checklist');
    } finally {
      setTasksLoading(false);
    }
  };

  const refreshTasks = async () => {
    if (!tasksUser) return;
    try {
      const res = await api.get(`/api/admin/users/${tasksUser.id}/onboarding`);
      setTasks(res.data);
    } catch { /* keep what we have */ }
  };

  const completeTask = async (key, label) => {
    if (!tasksUser) return;
    setTaskBusy(key);
    try {
      const res = await api.post(`/api/admin/users/${tasksUser.id}/onboarding/${key}`, { reason: 'Admin override' });
      toast.success(res.data.message || `${label} marked complete`);
      await refreshTasks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not complete the task');
    } finally {
      setTaskBusy('');
    }
  };

  const clearTask = async (key, label, emeralds) => {
    if (!tasksUser) return;
    if (!window.confirm(`Clear "${label}" for ${tasksUser.name}? Up to ${emeralds} Emeralds will be reversed and they can earn it again.`)) return;
    setTaskBusy(key);
    try {
      const res = await api.delete(`/api/admin/users/${tasksUser.id}/onboarding/${key}`);
      toast.success(res.data.message || `${label} cleared`);
      await refreshTasks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not clear the task');
    } finally {
      setTaskBusy('');
    }
  };

  const openBadgeModal = (user) => {
    setBadgeUser(user);
    setSelectedBadge(user.badge_type || '');
    setShowBadgeModal(true);
  };

  // Edit GEMS Tag function
  const openEditTagModal = (user) => {
    setEditingUser(user);
    setNewGemsTag(user.handle || '');
    setShowEditTagModal(true);
  };

  // Password reset functions
  const openPasswordModal = (user) => {
    setPasswordUser(user);
    setNewPassword('');
    setIsTemporary(false);
    setForceChange(false);
    setShowPasswordModal(true);
  };

  const generateTempPassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
    let pw = '';
    for (let i = 0; i < 12; i++) pw += chars.charAt(Math.floor(Math.random() * chars.length));
    setNewPassword(pw);
    setIsTemporary(true);
    setForceChange(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSavingPassword(true);
    try {
      const res = await api.post(`/api/admin/users/${passwordUser.id}/reset-password`, {
        new_password: newPassword,
        is_temporary: isTemporary,
        force_change_on_login: forceChange,
      });
      toast.success(res.data.message || 'Password reset successfully');
      setShowPasswordModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSaveGemsTag = async () => {
    if (!newGemsTag.trim()) {
      toast.error('Please enter a GEMS tag');
      return;
    }
    
    setSavingTag(true);
    try {
      await api.put(`/api/admin/users/${editingUser.id}/handle`, {
        user_id: editingUser.id,
        new_handle: newGemsTag.trim()
      });
      toast.success(`GEMS tag updated to @${newGemsTag.trim()}`);
      setShowEditTagModal(false);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update GEMS tag';
      toast.error(message);
    } finally {
      setSavingTag(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Records</h1>
          <p className="text-gray-500 text-sm mt-1">{total.toLocaleString()} total users</p>
        </div>
        <button
          onClick={exportUsers}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, or username..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              data-testid="users-search-input"
            />
          </div>
          {/* De-flood filter — default "Real" hides test / qa / bot fixtures. */}
          <select
            value={accountType}
            onChange={(e) => { setAccountType(e.target.value); setPage(1); }}
            className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            data-testid="users-account-type-filter"
          >
            <option value="real">Real (default)</option>
            <option value="test">Test</option>
            <option value="qa">QA</option>
            <option value="bot">Bot</option>
            <option value="internal">Internal</option>
            <option value="official">Official</option>
            <option value="all">All accounts</option>
          </select>
          <button
            type="submit"
            className="px-6 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
            data-testid="users-search-submit"
          >
            Search
          </button>
        </form>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Joined</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Stats</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan="6" className="py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-emerald-500 border-t-transparent"></div>
                      Loading users...
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-8 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-rose-400 flex items-center justify-center text-white font-medium">
                          {user.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{user.name}</p>
                          <p className="text-sm text-gray-500">@{user.handle}</p>
                          {(user.wallet_frozen || user.chargeback_count > 0) && (
                            <span
                              className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-red-100 text-red-700 border border-red-300"
                              data-testid={`user-frozen-badge-${user.id}`}
                              title={
                                user.wallet_frozen
                                  ? `Wallet frozen (${user.frozen_reason || 'chargeback'})`
                                  : `${user.chargeback_count} prior chargeback(s)`
                              }
                            >
                              {user.wallet_frozen ? 'FROZEN' : `CB×${user.chargeback_count}`}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{user.email}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3 text-sm">
                        <span className="text-emerald-600">{user.wins || 0}W</span>
                        <span className="text-rose-600">{user.losses || 0}L</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {user.is_suspended ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700">
                          Suspended
                        </span>
                      ) : user.is_admin ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                          Admin
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => viewUserDetail(user.id)}
                          className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEditTagModal(user)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit GEMS Tag"
                        >
                          <User className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openBadgeModal(user)}
                          className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                          title="Manage Badge"
                          data-testid={`badge-btn-${user.id}`}
                        >
                          <Award className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openTasksModal(user)}
                          className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                          title="Get Started tasks"
                          data-testid={`tasks-btn-${user.id}`}
                        >
                          <ListChecks className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openPasswordModal(user)}
                          className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          title="Reset Password"
                          data-testid={`password-btn-${user.id}`}
                        >
                          <KeyRound className="w-4 h-4" />
                        </button>
                        {!user.is_suspended ? (
                          <>
                            <button
                              onClick={() => suspendUser(user.id, 'temporary')}
                              className="p-2 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                              title="Suspend 7 days"
                              data-testid={`suspend-btn-${user.id}`}
                            >
                              <Ban className="w-4 h-4" />
                            </button>
                            {!user.is_admin && (
                              <button
                                onClick={() => banUser(user)}
                                className="p-2 text-gray-400 hover:text-white hover:bg-rose-700 rounded-lg transition-colors"
                                title="Ban (permanent)"
                                data-testid={`ban-btn-${user.id}`}
                              >
                                <ShieldOff className="w-4 h-4" />
                              </button>
                            )}
                          </>
                        ) : (
                          <button
                            onClick={() => unsuspendUser(user)}
                            className="p-2 text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
                            title="Unsuspend User"
                            data-testid={`unsuspend-btn-${user.id}`}
                          >
                            <UserCheck className="w-4 h-4" />
                          </button>
                        )}
                        {!user.is_admin && (
                          <button
                            onClick={() => deleteUser(user)}
                            className="p-2 text-gray-400 hover:text-white hover:bg-rose-600 rounded-lg transition-colors"
                            title="Delete User (permanent)"
                            data-testid={`delete-btn-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
          <p className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* User Detail Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">User Details</h2>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {/* User Info */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-rose-400 flex items-center justify-center text-white text-2xl font-bold">
                  {selectedUser.user?.name?.charAt(0) || '?'}
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{selectedUser.user?.name}</h3>
                  <p className="text-gray-500">{selectedUser.user?.email}</p>
                  <p className="text-sm text-gray-400">@{selectedUser.user?.handle}</p>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{selectedUser.user?.wins || 0}</p>
                  <p className="text-sm text-gray-500">Wins</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-rose-600">{selectedUser.user?.losses || 0}</p>
                  <p className="text-sm text-gray-500">Losses</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-amber-600">{selectedUser.user?.streak || 0}</p>
                  <p className="text-sm text-gray-500">Streak</p>
                </div>
              </div>

              {/* Badges */}
              {selectedUser.badges?.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Badges</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedUser.badges.map((badge, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700"
                      >
                        {badge}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Activity */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Recent Sessions</h4>
                <div className="space-y-2">
                  {selectedUser.recent_sessions?.slice(0, 5).map((session, i) => (
                    <div key={i} className="text-sm text-gray-600">
                      {new Date(session.created_at).toLocaleString()}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit GEMS Tag Modal */}
      {showEditTagModal && editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Edit GEMS Tag</h2>
                <button
                  onClick={() => setShowEditTagModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-rose-400 flex items-center justify-center text-white font-bold">
                  {editingUser.name?.charAt(0) || '?'}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{editingUser.name}</p>
                  <p className="text-sm text-gray-500">{editingUser.email}</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current GEMS Tag</label>
                <p className="text-gray-600 bg-gray-100 px-3 py-2 rounded-lg">@{editingUser.handle || 'Not set'}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New GEMS Tag</label>
                <div className="flex items-center">
                  <span className="px-3 py-2 bg-gray-100 border border-r-0 border-gray-200 rounded-l-lg text-gray-500">@</span>
                  <input
                    type="text"
                    value={newGemsTag}
                    onChange={(e) => setNewGemsTag(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                    placeholder="new_gems_tag"
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Only lowercase letters, numbers, and underscores allowed</p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> This change bypasses the one-time change restriction. Use sparingly.
                </p>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowEditTagModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveGemsTag}
                  disabled={savingTag || !newGemsTag.trim()}
                  className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingTag ? 'Saving...' : 'Save GEMS Tag'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Badge Management Modal */}
      {/* Round 15: Get Started checklist — complete a task by hand when the
          detector can't see it. Completing pays the member once; clearing
          reverses that payment. */}
      {showTasksModal && tasksUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] flex flex-col" data-testid="onboarding-tasks-modal">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <ListChecks className="w-5 h-5 text-emerald-600" /> Get Started Tasks
                </h2>
                <button
                  onClick={() => setShowTasksModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {tasksUser.name} {tasksUser.handle ? `· @${tasksUser.handle}` : ''}
              </p>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {tasksLoading && <p className="text-sm text-gray-500">Loading…</p>}
              {!tasksLoading && !tasks && <p className="text-sm text-gray-500">Nothing to show.</p>}
              {!tasksLoading && tasks && (
                <>
                  <div className="flex items-center justify-between mb-4 text-sm">
                    <span className="text-gray-600">
                      {tasks.tasks.filter(t => t.done).length}/{tasks.tasks.length} complete
                    </span>
                    <span className="font-semibold text-emerald-700">
                      {tasks.earned_emeralds}/{tasks.total_emeralds} Emeralds paid
                    </span>
                  </div>
                  <div className="space-y-2">
                    {tasks.tasks.map(t => (
                      <div
                        key={t.key}
                        className={`flex items-center gap-3 p-3 rounded-lg border ${t.done ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200'}`}
                        data-testid={`onboarding-task-${t.key}`}
                      >
                        {t.done
                          ? <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                          : <span className="w-4 h-4 rounded-full border border-gray-300 shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm font-medium ${t.done ? 'text-emerald-900' : 'text-gray-900'}`}>{t.label}</p>
                          <p className="text-xs text-gray-500">
                            +{t.emeralds} Emeralds{t.done && t.done_at ? ` · paid ${new Date(t.done_at).toLocaleDateString()}` : ''}
                          </p>
                        </div>
                        {t.done ? (
                          <button
                            onClick={() => clearTask(t.key, t.label, t.emeralds)}
                            disabled={taskBusy === t.key}
                            className="px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 flex items-center gap-1"
                            title="Clear and reverse the payment"
                          >
                            <RotateCcw className="w-3 h-3" /> Clear
                          </button>
                        ) : (
                          <button
                            onClick={() => completeTask(t.key, t.label)}
                            disabled={taskBusy === t.key}
                            className="px-3 py-1.5 text-xs font-semibold text-white bg-emerald-500 hover:bg-emerald-600 rounded-lg disabled:opacity-50"
                            data-testid={`complete-task-${t.key}`}
                          >
                            {taskBusy === t.key ? 'Working…' : 'Mark complete'}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-4">
                    Tasks the member has already satisfied are detected automatically when this opens.
                    Marking one by hand pays the Emeralds once and is recorded in the admin log.
                  </p>
                </>
              )}
            </div>

            <div className="p-4 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setShowTasksModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {showBadgeModal && badgeUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Manage Verified Badge</h2>
                <button
                  onClick={() => setShowBadgeModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-rose-400 flex items-center justify-center text-white font-bold">
                  {badgeUser.name?.charAt(0) || '?'}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{badgeUser.name}</p>
                  <p className="text-sm text-gray-500">@{badgeUser.handle}</p>
                  {badgeUser.badge_type && (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${
                      badgeUser.badge_type === 'sapphire' ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      Current: {badgeUser.badge_type}
                    </span>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Select Badge Type</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="badge"
                      value=""
                      checked={selectedBadge === ''}
                      onChange={(e) => setSelectedBadge(e.target.value)}
                      className="w-4 h-4 text-emerald-600"
                    />
                    <div>
                      <p className="font-medium text-gray-900">No Badge</p>
                      <p className="text-sm text-gray-500">Remove verified badge</p>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 p-3 border border-sky-200 rounded-lg cursor-pointer hover:bg-sky-50 bg-sky-50/50">
                    <input
                      type="radio"
                      name="badge"
                      value="sapphire"
                      checked={selectedBadge === 'sapphire'}
                      onChange={(e) => setSelectedBadge(e.target.value)}
                      className="w-4 h-4 text-sky-600"
                    />
                    <div className="flex items-center gap-2">
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                        <defs>
                          <linearGradient id="sapphireGradAdmin" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#60A5FA" />
                            <stop offset="100%" stopColor="#1D4ED8" />
                          </linearGradient>
                        </defs>
                        <path d="M12 2L5 7L12 22L19 7L12 2Z" fill="url(#sapphireGradAdmin)" stroke="#1E40AF" strokeWidth="0.5"/>
                      </svg>
                      <div>
                        <p className="font-medium text-sky-900">Sapphire - Celebrity Verified</p>
                        <p className="text-sm text-sky-600">For verified celebrities and public figures</p>
                      </div>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 p-3 border border-amber-200 rounded-lg cursor-pointer hover:bg-amber-50 bg-amber-50/50">
                    <input
                      type="radio"
                      name="badge"
                      value="canary"
                      checked={selectedBadge === 'canary'}
                      onChange={(e) => setSelectedBadge(e.target.value)}
                      className="w-4 h-4 text-amber-600"
                    />
                    <div className="flex items-center gap-2">
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                        <defs>
                          <linearGradient id="canaryGradAdmin" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#FDE047" />
                            <stop offset="100%" stopColor="#EAB308" />
                          </linearGradient>
                        </defs>
                        <path d="M12 2L5 7L12 22L19 7L12 2Z" fill="url(#canaryGradAdmin)" stroke="#A16207" strokeWidth="0.5"/>
                        <path d="M8 12L11 15L16 9" stroke="#713F12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <div>
                        <p className="font-medium text-amber-900">Canary - Official GEMS Live</p>
                        <p className="text-sm text-amber-600">For GEMS Live staff and official accounts</p>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowBadgeModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => assignBadge(badgeUser.id, selectedBadge)}
                  disabled={savingBadge}
                  className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingBadge ? 'Saving...' : 'Save Badge'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Password Reset Modal */}
      {showPasswordModal && passwordUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full" data-testid="password-reset-modal">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Reset Password</h2>
                <button onClick={() => setShowPasswordModal(false)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700">×</button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-indigo-400 flex items-center justify-center text-white font-bold">
                  {passwordUser.name?.charAt(0) || '?'}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{passwordUser.name}</p>
                  <p className="text-sm text-gray-500">@{passwordUser.handle} — {passwordUser.email}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="new-password-input"
                  />
                  <button
                    onClick={generateTempPassword}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium whitespace-nowrap"
                    data-testid="generate-temp-btn"
                  >
                    Generate Temp
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Min 6 characters</p>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={isTemporary}
                    onChange={(e) => { setIsTemporary(e.target.checked); if (e.target.checked) setForceChange(true); }}
                    className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    data-testid="temp-password-toggle"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-800">Temporary Password</p>
                    <p className="text-xs text-gray-500">Mark as temporary (user should change on next login)</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={forceChange}
                    onChange={(e) => setForceChange(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    data-testid="force-change-toggle"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-800">Force Change on Login</p>
                    <p className="text-xs text-gray-500">Require user to set a new password after logging in</p>
                  </div>
                </label>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800"><strong>Warning:</strong> This will immediately change the user's password. The user will need the new password to log in.</p>
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowPasswordModal(false)} className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">Cancel</button>
                <button
                  onClick={handleResetPassword}
                  disabled={savingPassword || !newPassword || newPassword.length < 6}
                  className="flex-1 px-4 py-2 bg-purple-700 text-white rounded-lg hover:bg-purple-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="save-password-btn"
                >
                  {savingPassword ? 'Resetting...' : isTemporary ? 'Set Temporary Password' : 'Set Permanent Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
